import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { OrgChartEmployee } from '@lms/shared-types'
import { getOrgChart } from '../../../api/hr-org-chart'
import {
  listApproverOverrides,
  type ApproverOverride,
} from '../../../api/approver-overrides'
import { fmtDate } from '../../../lib/format'
import {
  removePrimaryApprover,
  setPrimaryApprover,
} from '../../../api/hr-approval-assignments'
import { Card } from '../../primitives/Card'
import { Button } from '../../primitives/Button'
import { Avatar } from '../../primitives/Avatar'
import { Banner } from '../../primitives/Banner'
import { Modal } from '../../primitives/Modal'
import { ThinkingDots } from '../../primitives/ThinkingDots'
import { ConfirmationDialog } from '../../primitives/ConfirmationDialog'
import { useToast } from '../../primitives/Toast'
import { roleLabel } from '../../../lib/role'
import { getEmployee } from '../../../api/employees'
import { listTeams } from '../../../api/teams'
import { EmployeeForm } from '../../../pages/HrEmployeesPage'
import { AssignApproverDialog, type NamedPerson } from './AssignApproverDialog'

interface ApproverGroup {
  approver: OrgChartEmployee
  members: OrgChartEmployee[]
}

// Sentinel selection key for the "Unassigned" bucket row.
const UNASSIGNED_KEY = '__unassigned__'

// Queries whose results depend on the approval graph — invalidated after any
// assignment change so the roster / rhythm / org chart all re-derive.
const VISIBILITY_QUERY_KEYS = [
  ['org-chart'],
  ['manager-team-roster'],
  ['patterns-team'],
] as const

/**
 * Approvers tab (Manage Structure), as a master-detail like the Rhythm board:
 * a left list of approvers (plus an "Unassigned" bucket), and a right panel
 * that lists the selected approver's members. Actions: add a member to an
 * approver, remove a member (leaving them unassigned), make someone an
 * approver, and assign an unassigned person. Runs on the flat
 * one-approver-per-employee model, so assigning a member inherently moves them
 * off their previous approver (warned before confirm).
 */
export function ApproversTab(): JSX.Element {
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const orgQuery = useQuery({
    queryKey: ['org-chart'],
    queryFn: ({ signal }) => getOrgChart(signal),
  })
  // Active approver covers (the "Approver cover" / delegation feature), so an
  // approver whose inbox is currently rerouted reads as "Covered" (chip on the
  // row + details in the panel) — a temporary, time-bound overlay on top of
  // the standing approver structure.
  const coversQuery = useQuery({
    queryKey: ['approver-overrides', 'active'],
    queryFn: () => listApproverOverrides({ activeOnly: true }),
  })

  // Selected left-list entry: an approver id, the UNASSIGNED sentinel, or null.
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  // Assign dialog: null = closed; the presets decide which flow it drives.
  const [assignState, setAssignState] = useState<{
    presetApprover?: NamedPerson
    presetMember?: NamedPerson
  } | null>(null)
  // Remove-member confirmation.
  const [removeState, setRemoveState] = useState<{
    member: NamedPerson
    approverName: string
  } | null>(null)
  // Left-list name filter.
  const [nameQuery, setNameQuery] = useState('')
  // Edit-user modal (change role etc.) for the selected approver.
  const [editUserId, setEditUserId] = useState<string | null>(null)

  // Fit the panels to the viewport (no page scroll): measure the grid's top and
  // fill the space down to the bottom margin. Recomputes on resize + once the
  // data arrives and the grid actually mounts.
  const gridRef = useRef<HTMLDivElement>(null)
  const [panelHeight, setPanelHeight] = useState<number | undefined>(undefined)
  useLayoutEffect(() => {
    const el = gridRef.current
    if (!el) return
    const compute = (): void => {
      const top = el.getBoundingClientRect().top
      setPanelHeight(Math.max(360, window.innerHeight - top - 24))
    }
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [orgQuery.isSuccess])

  const invalidate = (): void => {
    for (const key of VISIBILITY_QUERY_KEYS) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const assignMutation = useMutation({
    mutationFn: (vars: { memberId: string; approverId: string }) =>
      setPrimaryApprover(vars.memberId, vars.approverId),
    onSuccess: (_data, vars) => {
      setAssignState(null)
      // Keep the approver we just assigned to selected, so its members show.
      setSelectedKey(vars.approverId)
      notify('Approver updated', 'success')
      invalidate()
    },
    onError: (err) => {
      notify(err instanceof Error ? err.message : 'Could not update approver', 'error')
    },
  })

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => removePrimaryApprover(memberId),
    onSuccess: () => {
      setRemoveState(null)
      notify('Member removed — now unassigned', 'success')
      invalidate()
    },
    onError: (err) => {
      notify(err instanceof Error ? err.message : 'Could not remove member', 'error')
    },
  })

  const { groups, unassigned, currentApproverName } = useMemo(() => {
    const data = orgQuery.data
    if (!data) {
      return {
        groups: [] as ApproverGroup[],
        unassigned: [] as OrgChartEmployee[],
        currentApproverName: (): string | null => null,
      }
    }
    const empById = new Map(data.employees.map((e) => [e.id, e]))
    const nameFor = (id: string): string | null => empById.get(id)?.name ?? null

    const built: ApproverGroup[] = []
    for (const [approverId, memberIds] of Object.entries(
      data.directReportsByApprover,
    )) {
      const approver = empById.get(approverId)
      if (!approver) continue
      const members = memberIds
        .map((id) => empById.get(id))
        .filter((e): e is OrgChartEmployee => e != null)
        .sort((a, b) => a.name.localeCompare(b.name))
      built.push({ approver, members })
    }
    built.sort((a, b) => a.approver.name.localeCompare(b.approver.name))

    const approverIds = new Set(built.map((g) => g.approver.id))
    // Unassigned = no approver AND not themselves an approver (top-of-org
    // approvers legitimately have no approver but belong in the list above).
    const unassignedList = data.employees
      .filter(
        (e) =>
          data.primaryApproverByEmployee[e.id] == null && !approverIds.has(e.id),
      )
      .sort((a, b) => a.name.localeCompare(b.name))

    const currentName = (employeeId: string): string | null => {
      const aid = data.primaryApproverByEmployee[employeeId]
      return aid ? nameFor(aid) : null
    }

    return {
      groups: built,
      unassigned: unassignedList,
      currentApproverName: currentName,
    }
  }, [orgQuery.data])

  // Active covers keyed by the covered approver, so a row/panel can show who
  // is standing in and for how long.
  const coversByApprover = useMemo(() => {
    const map = new Map<string, ApproverOverride[]>()
    for (const c of coversQuery.data ?? []) {
      const list = map.get(c.coveredApproverId) ?? []
      list.push(c)
      map.set(c.coveredApproverId, list)
    }
    return map
  }, [coversQuery.data])

  const selectedGroup =
    selectedKey && selectedKey !== UNASSIGNED_KEY
      ? groups.find((g) => g.approver.id === selectedKey) ?? null
      : null
  const showUnassignedDetail =
    selectedKey === UNASSIGNED_KEY && unassigned.length > 0
  const detailOpen = selectedGroup != null || showUnassignedDetail

  const nameFilter = nameQuery.trim().toLowerCase()
  const visibleGroups = nameFilter
    ? groups.filter((g) => g.approver.name.toLowerCase().includes(nameFilter))
    : groups

  if (orgQuery.isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <ThinkingDots label="Loading approvers" />
      </div>
    )
  }
  if (orgQuery.isError) {
    return (
      <Banner
        kind="warning"
        title="Couldn't load approvers"
        subtitle="Please try again."
      />
    )
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-3)', maxWidth: 620 }}>
          Who approves whose leave. Select an approver to see their members.
          Adding a member moves them off their current approver; removing a
          member leaves them unassigned until you give them a new approver.
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setAssignState({})}
          data-testid="approvers-make-approver"
        >
          Make approver
        </Button>
      </div>

      {groups.length === 0 && unassigned.length === 0 ? (
        <Card padding={24}>
          <div style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>
            No approvers yet. Use “Make approver” to assign the first one.
          </div>
        </Card>
      ) : (
        <div
          ref={gridRef}
          style={{
            display: 'grid',
            gridTemplateColumns: detailOpen ? 'minmax(260px, 340px) 1fr' : '1fr',
            gap: 0,
            alignItems: 'stretch',
            transition: 'grid-template-columns 160ms ease',
            // Fill the viewport below the page header/tabs so both panes are
            // full-height and scroll internally rather than growing the page.
            height: panelHeight,
            minHeight: 360,
          }}
        >
          {/* ── Left: approver list ─────────────────────────────────────── */}
          <div
            data-testid="approvers-list"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRightWidth: detailOpen ? 2 : 1,
              borderRadius: 12,
              borderTopRightRadius: detailOpen ? 0 : 12,
              borderBottomRightRadius: detailOpen ? 0 : 12,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: '100%',
            }}
          >
            <div style={listHeaderColumnStyle}>
              <span style={{ letterSpacing: '0.04em' }}>
                Approvers · {groups.length}
              </span>
              <input
                type="search"
                aria-label="Search approvers by name"
                data-testid="approvers-search"
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                placeholder="Search by name"
                style={{
                  width: '100%',
                  // Cap it so it stays compact when the list is full-width; it
                  // still fills the narrow column once a panel is open.
                  maxWidth: 320,
                  padding: '7px 10px',
                  fontSize: 13,
                  fontWeight: 400,
                  textTransform: 'none',
                  letterSpacing: 0,
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {visibleGroups.length === 0 ? (
                <div
                  style={{
                    padding: 14,
                    fontSize: 12.5,
                    color: 'var(--text-3)',
                    textAlign: 'center',
                  }}
                >
                  No approvers match “{nameQuery.trim()}”.
                </div>
              ) : null}
              {visibleGroups.map((g) => (
                <ListRow
                  key={g.approver.id}
                  name={g.approver.name}
                  sub={`${roleLabel(g.approver.role)} · ${g.members.length} ${
                    g.members.length === 1 ? 'report' : 'reports'
                  }`}
                  active={selectedKey === g.approver.id}
                  covered={coversByApprover.has(g.approver.id)}
                  onClick={() =>
                    setSelectedKey((cur) =>
                      cur === g.approver.id ? null : g.approver.id,
                    )
                  }
                  testId={`approvers-list-row-${g.approver.id}`}
                />
              ))}
              {unassigned.length > 0 ? (
                <ListRow
                  name={`Unassigned · no approver`}
                  sub={`${unassigned.length} ${
                    unassigned.length === 1 ? 'person' : 'people'
                  }`}
                  active={selectedKey === UNASSIGNED_KEY}
                  onClick={() =>
                    setSelectedKey((cur) =>
                      cur === UNASSIGNED_KEY ? null : UNASSIGNED_KEY,
                    )
                  }
                  testId="approvers-list-row-unassigned"
                  danger
                />
              ) : null}
            </div>
          </div>

          {/* ── Right: detail panel ─────────────────────────────────────── */}
          {selectedGroup ? (
            <DetailPanel
              title={selectedGroup.approver.name}
              subtitle={`${roleLabel(selectedGroup.approver.role)} · ${
                selectedGroup.members.length
              } ${selectedGroup.members.length === 1 ? 'report' : 'reports'}`}
              action={
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setEditUserId(selectedGroup.approver.id)}
                    data-testid="approvers-edit-user"
                  >
                    Edit user
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setAssignState({
                        presetApprover: {
                          id: selectedGroup.approver.id,
                          name: selectedGroup.approver.name,
                        },
                      })
                    }
                    data-testid="approvers-add-member"
                  >
                    Add member
                  </Button>
                </div>
              }
            >
              <CoverNote
                covers={coversByApprover.get(selectedGroup.approver.id) ?? []}
              />
              {selectedGroup.members.map((m) => (
                <PersonRow
                  key={m.id}
                  name={m.name}
                  sub={`${m.teamName} · ${m.jobTitle}`}
                  testId={`approver-member-${m.id}`}
                  action={
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() =>
                        setRemoveState({
                          member: { id: m.id, name: m.name },
                          approverName: selectedGroup.approver.name,
                        })
                      }
                      data-testid={`approver-remove-member-${m.id}`}
                    >
                      Remove
                    </Button>
                  }
                />
              ))}
            </DetailPanel>
          ) : showUnassignedDetail ? (
            <DetailPanel
              title="Unassigned"
              subtitle="These people have no approver — their leave can’t route until assigned."
            >
              {unassigned.map((emp) => (
                <PersonRow
                  key={emp.id}
                  name={emp.name}
                  sub={`${emp.teamName} · ${emp.jobTitle}`}
                  testId={`approvers-unassigned-${emp.id}`}
                  action={
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setAssignState({
                          presetMember: { id: emp.id, name: emp.name },
                        })
                      }
                      data-testid={`approvers-assign-${emp.id}`}
                    >
                      Assign approver
                    </Button>
                  }
                />
              ))}
            </DetailPanel>
          ) : null}
        </div>
      )}

      <AssignApproverDialog
        open={assignState !== null}
        onClose={() => setAssignState(null)}
        onConfirm={(memberId, approverId) =>
          assignMutation.mutate({ memberId, approverId })
        }
        loading={assignMutation.isPending}
        presetApprover={assignState?.presetApprover}
        presetMember={assignState?.presetMember}
        currentApproverName={currentApproverName}
      />

      {removeState !== null ? (
        <ConfirmationDialog
          open
          onClose={() => setRemoveState(null)}
          onConfirm={() => removeMutation.mutate(removeState.member.id)}
          title="Remove member?"
          subtitle={`${removeState.member.name} will no longer report to ${removeState.approverName}.`}
          confirmLabel="Remove"
          variant="danger"
          loading={removeMutation.isPending}
          data-testid="approvers-remove-dialog"
        >
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
            They will have <strong>no approver</strong> until you assign a new one.
            Their leave requests can’t be routed for approval while unassigned.
          </p>
        </ConfirmationDialog>
      ) : null}

      {editUserId !== null ? (
        <EditUserModal
          employeeId={editUserId}
          onClose={() => setEditUserId(null)}
          onSaved={() => {
            // Refresh the edited employee's detail + the directory so a
            // reopen shows the new role (not the cached pre-edit one), plus
            // the approval-graph views since role can shift visibility.
            void queryClient.invalidateQueries({ queryKey: ['employee'] })
            void queryClient.invalidateQueries({ queryKey: ['employees'] })
            setEditUserId(null)
            notify('Employee updated', 'success')
            invalidate()
          }}
        />
      ) : null}
    </div>
  )
}

// ── Edit-user modal (reuses the Employees page form) ──────────────────────────

interface EditUserModalProps {
  employeeId: string
  onClose: () => void
  onSaved: () => void
}

function EditUserModal({
  employeeId,
  onClose,
  onSaved,
}: EditUserModalProps): JSX.Element {
  const empQuery = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => getEmployee(employeeId),
    // Always refetch on open so a re-edit reflects the latest role, not the
    // pre-edit snapshot in cache.
    refetchOnMount: 'always',
  })
  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: listTeams })
  // Wait for the (re)fetch to settle before rendering the form — EmployeeForm
  // snapshots its fields once on mount, so it must mount with fresh data.
  const ready =
    empQuery.data != null && !empQuery.isFetching && teamsQuery.data != null

  return (
    <Modal
      open
      onClose={onClose}
      title={empQuery.data ? `Edit · ${empQuery.data.name}` : 'Edit user'}
      maxWidth={640}
    >
      {ready ? (
        <EmployeeForm
          mode="edit"
          employee={empQuery.data}
          teams={teamsQuery.data}
          onClose={onClose}
          onSaved={onSaved}
        />
      ) : (
        <div style={{ padding: 24 }}>
          <ThinkingDots label="Loading employee" />
        </div>
      )}
    </Modal>
  )
}

// ── Left list row ───────────────────────────────────────────────────────────

interface ListRowProps {
  name: string
  sub: string
  active: boolean
  onClick: () => void
  testId: string
  danger?: boolean
  /** Approver's inbox is currently covered (rerouted) — surfaces a chip. */
  covered?: boolean
}

function ListRow({
  name,
  sub,
  active,
  onClick,
  testId,
  danger = false,
  covered = false,
}: ListRowProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-current={active ? 'true' : undefined}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        padding: '12px 14px',
        width: '100%',
        textAlign: 'left',
        background: active ? 'var(--baby-blue-tint)' : 'transparent',
        borderTop: '1px solid var(--border)',
        borderLeft: `3px solid ${active ? 'var(--steel-blue)' : 'transparent'}`,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {danger ? (
        <span
          aria-hidden
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          !
        </span>
      ) : (
        <Avatar name={name} size={30} />
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: danger ? 'var(--danger-fg)' : 'var(--text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </span>
          {covered ? <CoveredChip /> : null}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{sub}</div>
      </div>
    </button>
  )
}

function CoveredChip(): JSX.Element {
  return (
    <span
      data-testid="approvers-covered-chip"
      style={{
        flexShrink: 0,
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: 'var(--warning-fg)',
        background: 'var(--warning-bg)',
        border: '1px solid var(--warning-line)',
        borderRadius: 999,
        padding: '1px 6px',
        lineHeight: 1.4,
      }}
    >
      Covered
    </span>
  )
}

// ── Active approver-cover note (right panel) ──────────────────────────────────

interface CoverNoteProps {
  covers: ReadonlyArray<ApproverOverride>
}

function CoverNote({ covers }: CoverNoteProps): JSX.Element | null {
  if (covers.length === 0) return null
  return (
    <div
      data-testid="approvers-cover-note"
      style={{
        margin: '14px 28px 4px',
        padding: '10px 12px',
        background: 'var(--warning-bg)',
        border: '1px solid var(--warning-line)',
        borderRadius: 10,
        fontSize: 12.5,
        color: 'var(--text-2)',
        lineHeight: 1.5,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--warning-fg)',
          marginBottom: 4,
        }}
      >
        Inbox covered
      </div>
      {covers.map((c) => (
        <div key={c.id}>
          Covered by <strong>{c.coverApproverName || 'Unknown'}</strong> ·{' '}
          {fmtDate(c.startsOn)} → {fmtDate(c.endsOn)}
        </div>
      ))}
    </div>
  )
}

// ── Right detail panel shell ─────────────────────────────────────────────────

interface DetailPanelProps {
  title: string
  subtitle: string
  action?: React.ReactNode
  children: React.ReactNode
}

function DetailPanel({
  title,
  subtitle,
  action,
  children,
}: DetailPanelProps): JSX.Element {
  return (
    <div
      data-testid="approvers-detail"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeft: 'none',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderTopRightRadius: 12,
        borderBottomRightRadius: 12,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
      }}
    >
      <div style={{ ...listHeaderStyle, gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', textTransform: 'none' }}>
            {title}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-3)',
              fontWeight: 400,
              textTransform: 'none',
              letterSpacing: 0,
            }}
          >
            {subtitle}
          </div>
        </div>
        {action}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>
    </div>
  )
}

// ── A person row inside the detail panel ──────────────────────────────────────

interface PersonRowProps {
  name: string
  sub: string
  action: React.ReactNode
  testId: string
}

function PersonRow({ name, sub, action, testId }: PersonRowProps): JSX.Element {
  return (
    <div
      role="listitem"
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        // Plain list (no row dividers) with generous horizontal breathing room.
        padding: '13px 28px',
      }}
    >
      <Avatar name={name} size={30} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
          {name}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{sub}</div>
      </div>
      {action}
    </div>
  )
}

// Both panes share this header height so their bottom borders line up: the
// left header stacks a label + search, the detail header centres its
// title/subtitle/action in the same height.
const HEADER_HEIGHT = 84
const listHeaderBase: React.CSSProperties = {
  minHeight: HEADER_HEIGHT,
  boxSizing: 'border-box',
  background: 'var(--surface-2)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-2)',
}
// Detail-pane header: single centred row.
const listHeaderStyle: React.CSSProperties = {
  ...listHeaderBase,
  padding: '0 28px',
  display: 'flex',
  alignItems: 'center',
}
// Left-list header: label + search stacked, vertically centred.
const listHeaderColumnStyle: React.CSSProperties = {
  ...listHeaderBase,
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 8,
}
